// ./src/utils/api/AccountEmailService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";

// GraphQL queries
const ACCOUNT_EMAIL_LIST_QUERY = gql`
  query AccountEmailList(
    $filter: AccountEmailFilter
    $params: PaginationAndSorting
  ) {
    accountEmailList(filter: $filter, params: $params) {
      code
      data {
        id
        name
        email
        description
        confirmationAttempt
        expiresAt
        confirmedAt
        lastConfirmationSentAt
        status
        rights
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ACCOUNT_EMAIL_GET_QUERY = gql`
  query AccountEmailGet($id: ID) {
    accountEmailGet(id: $id) {
      code
      data {
        id
        name
        email
        description
        confirmationAttempt
        expiresAt
        confirmedAt
        lastConfirmationSentAt
        status
        rights
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ACCOUNT_EMAIL_GET_MANY_QUERY = gql`
  query AccountEmailGetMany($ids: [ID]) {
    accountEmailGetMany(ids: $ids) {
      code
      data {
        id
        name
        email
        description
        confirmationAttempt
        expiresAt
        confirmedAt
        lastConfirmationSentAt
        status
        rights
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const ACCOUNT_EMAIL_CREATE_MUTATION = gql`
  mutation AccountEmailCreate($params: AccountEmailInput) {
    accountEmailCreate(params: $params) {
      code
      data {
        id
        name
        email
        description
        confirmationAttempt
        expiresAt
        confirmedAt
        lastConfirmationSentAt
        status
        rights
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ACCOUNT_EMAIL_UPDATE_MUTATION = gql`
  mutation AccountEmailUpdate(
    $where: AccountEmailWhereInput
    $params: AccountEmailInput
  ) {
    accountEmailUpdate(where: $where, params: $params) {
      code
      data {
        id
        name
        email
        description
        confirmationAttempt
        expiresAt
        confirmedAt
        lastConfirmationSentAt
        status
        rights
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ACCOUNT_EMAIL_REMOVE_MUTATION = gql`
  mutation AccountEmailRemove($where: AccountEmailWhereInput) {
    accountEmailRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ACCOUNT_EMAIL_REMOVE_MANY_MUTATION = gql`
  mutation AccountEmailRemoveMany($where: [AccountEmailWhereInput]) {
    accountEmailRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ACCOUNT_EMAIL_SEND_CONFIRMATION_CODE_MUTATION = gql`
  mutation AccountEmailSendConfirmationCode($id: ID) {
    accountEmailSendConfirmationCode(id: $id) {
      code
      data {
        id
        name
        email
        description
        confirmationAttempt
        expiresAt
        confirmedAt
        lastConfirmationSentAt
        status
        rights
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ACCOUNT_EMAIL_VERIFY_CODE_MUTATION = gql`
  mutation AccountEmailVerifyCode($params: AccountEmailVerifyCodeInput) {
    accountEmailVerifyCode(params: $params) {
      code
      data {
        id
        name
        email
        description
        confirmationAttempt
        expiresAt
        confirmedAt
        lastConfirmationSentAt
        status
        rights
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface AccountEmail {
  id?: string | undefined;
  accountId?: string | undefined;
  name?: string | undefined;
  email?: string | undefined;
  description?: string | undefined;
  confirmationAttempt?: number | undefined;
  expiresAt?: string | undefined;
  confirmedAt?: string | undefined;
  lastConfirmationSentAt?: string | undefined;
  status?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  removedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  userCreated?: User;
  userUpdated?: User;
  userRemoved?: User;
  rights?: string[];
}

export interface AccountEmailInput {
  accountId?: string | null | undefined;
  name?: string | null | undefined;
  email?: string | null | undefined;
  description?: string | null | undefined;
  status?: number | null | undefined;
}

export interface AccountEmailVerifyCodeInput {
  id?: string | null | undefined;
  code?: string | null | undefined;
}

export interface AccountEmailFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  email?: string[] | null | undefined;
  isConfirmed?: boolean | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface AccountEmailWhereInput {
  id?: string | undefined;
}

/**
 * AccountEmail service that handles all account email-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 * Plus confirmation operations: sendConfirmationCode, verifyCode
 */
export class AccountEmailService {
  /**
   * Get a list of account emails with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: AccountEmailFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(ACCOUNT_EMAIL_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single account email by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(ACCOUNT_EMAIL_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple account emails by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(ACCOUNT_EMAIL_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new account email
   */
  static async create(params: AccountEmailInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      ACCOUNT_EMAIL_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing account email
   */
  static async update(
    where: AccountEmailWhereInput,
    params: AccountEmailInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      ACCOUNT_EMAIL_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete an account email
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      ACCOUNT_EMAIL_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple account emails
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      ACCOUNT_EMAIL_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }

  /**
   * Send confirmation code to the account email
   */
  static async sendConfirmationCode(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      ACCOUNT_EMAIL_SEND_CONFIRMATION_CODE_MUTATION,
      {
        id,
      }
    );
    return result;
  }

  /**
   * Verify the confirmation code for an account email
   */
  static async verifyCode(
    params: AccountEmailVerifyCodeInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      ACCOUNT_EMAIL_VERIFY_CODE_MUTATION,
      {
        params,
      }
    );
    return result;
  }
}
